/**
 * Who is in the tournament, and the one action that makes sense for them right
 * now.
 *
 * The action is contextual because the two are not alternatives an admin
 * chooses between — which one applies is decided by whether a draw exists:
 *
 *   registration — Remove entry. The entrant is deleted and the spot is
 *                  offered to the waitlist. DELETE /entries/:id enforces this
 *                  window server-side, so the button matches the endpoint
 *                  rather than guessing.
 *   after that   — Forfeit from night. Not the same act: match rows now point
 *                  at this entry, so it is conceded, not erased. One POST to
 *                  night-forfeit concedes every undecided match the entrant
 *                  has left, in a single transaction.
 *
 * The forfeit confirms in place. It cannot be undone and, unlike a removal, it
 * hands wins to other players — so it does not fire on a single click.
 *
 * A forfeited entrant STAYS ON THIS LIST, marked withdrawn. They played, their
 * results stand, and a row that vanished would leave an admin unable to tell
 * "left early" from "never entered". The server marks them with withdrawn_at
 * and releases their pending marker jobs; the only thing left to do here is say
 * so. Note this is the NIGHT forfeit — a single match-forfeit, on Boards and
 * Fixtures, changes no status at all.
 */

import { useState } from 'react';
import { apiDel, apiPost } from '../../lib/api.js';

/** The endpoint refuses a delete outside this status; the button follows it. */
const REMOVABLE_STATUS = 'registration';

/** "1 match" / "3 matches", so the summary line does not read like a robot. */
function matchCount(n) {
  return `${n} ${n === 1 ? 'match' : 'matches'}`;
}

export default function EntriesTab({ venueId, tournamentId, tournament, entries, groupByEntry, onChanged }) {
  const [busyId, setBusyId]       = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [error, setError]         = useState(null);
  const [result, setResult]       = useState(null);

  const canRemove = tournament.status === REMOVABLE_STATUS;

  const remove = async (entry) => {
    setBusyId(entry.id);
    setError(null);

    const { error: err } = await apiDel(
      `/api/venues/${venueId}/tournaments/${tournamentId}/entries/${entry.id}`
    );

    setBusyId(null);
    if (err) { setError(err); return; }
    await onChanged();
  };

  const forfeitNight = async (entry) => {
    setBusyId(entry.id);
    setError(null);
    setResult(null);

    // One call, not one per match: the server loops inside a single batch so a
    // half-forfeited entrant is not a state this page can produce.
    const { data, error: err } = await apiPost(
      `/api/venues/${venueId}/tournaments/${tournamentId}/night-forfeit`,
      { entryId: entry.id }
    );

    setBusyId(null);
    setConfirmId(null);
    if (err) { setError(err); return; }

    setResult({ name: entry.display_name, ...data });
    await onChanged();
  };

  if (entries.length === 0) {
    return <div className="hd-empty">No entries yet.</div>;
  }

  return (
    <div className="hd-stack">
      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      {result && (
        <div className="hd-badge hd-badge-info" role="status">
          {result.forfeited.length === 0
            ? `${result.name} had no undecided matches left, and is marked withdrawn.`
            : `${result.name} forfeited ${matchCount(result.forfeited.length)} and is marked withdrawn.`}
          {result.markersCleared > 0
            && ` ${result.markersCleared} marker assignment(s) released.`}
          {result.skipped.length > 0 && ` ${matchCount(result.skipped.length)} left alone: `
            + `${[...new Set(result.skipped.map((s) => s.reason))].join('; ')}.`}
        </div>
      )}

      {result?.warnings?.length > 0 && (
        <div className="hd-badge hd-badge-danger" role="alert">
          {result.warnings.map((w) => `Match ${w.matchId}: ${w.reason}`).join(' ')}
        </div>
      )}

      <div className="hd-table-wrap">
        <table className="hd-table hd-admin-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Status</th>
              <th>Group</th>
              <th className="hd-table-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.display_name}</td>
                <td>
                  {/* Withdrawn outranks guest/confirmed: it is the thing an
                      admin scanning this column actually needs to see. */}
                  {entry.withdrawn_at ? (
                    <span className="hd-badge hd-badge-danger">Withdrawn</span>
                  ) : (
                    <span className={`hd-badge hd-badge-${entry.is_guest ? 'neutral' : 'success'}`}>
                      {entry.is_guest ? 'Guest' : 'Confirmed'}
                    </span>
                  )}
                </td>
                <td>
                  {groupByEntry[entry.id]
                    ? `Group ${groupByEntry[entry.id]}`
                    : <span className="hd-muted">—</span>}
                </td>
                <td className="hd-table-right">
                  {/* Static text, not a disabled button: there is no state in
                      which it becomes available again. */}
                  {entry.withdrawn_at ? (
                    <span className="hd-muted">Left for the night</span>
                  ) : canRemove ? (
                    <button
                      type="button"
                      className="hd-btn hd-btn-quiet hd-btn-sm"
                      disabled={busyId === entry.id}
                      onClick={() => remove(entry)}
                    >
                      {busyId === entry.id ? 'Removing…' : 'Remove entry'}
                    </button>
                  ) : confirmId === entry.id ? (
                    <span className="hd-row" style={{ justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="hd-btn hd-btn-secondary hd-btn-sm"
                        disabled={busyId === entry.id}
                        onClick={() => forfeitNight(entry)}
                      >
                        {busyId === entry.id ? 'Forfeiting…' : 'Confirm forfeit'}
                      </button>
                      <button
                        type="button"
                        className="hd-btn hd-btn-quiet hd-btn-sm"
                        disabled={busyId === entry.id}
                        onClick={() => setConfirmId(null)}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="hd-btn hd-btn-quiet hd-btn-sm"
                      disabled={busyId != null}
                      onClick={() => { setConfirmId(entry.id); setError(null); setResult(null); }}
                    >
                      Forfeit from night
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!canRemove && (
        <p className="hd-hint">
          Entries can only be removed while registration is open. Once a draw
          exists a player is forfeited, not deleted — every match of theirs the
          night has not yet decided is conceded, and each opponent takes the win.
        </p>
      )}
    </div>
  );
}
